// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import Image from '@tcp/core/src/components/common/atoms/Image';
import logger from '@tcp/core/src/utils/loggerInstance';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import {
  getLabelFromCopyOrFromTranslation,
  renderLabel,
  getDynamicLabelValues,
} from '@tcp/core/src/utils/labels/labelUtilty.js';
import {
  getIconPath,
  isCanada,
  dateFormatter,
  getFormattedPreOrderDate,
  getDateObject,
  isMobileWeb,
} from '@tcp/core/src/utils';

import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import DeliveryMethods from '@tcp/core/src/components/common/molecules/DeliveryMethods/views';
import CARTPAGE_CONSTANTS from '../../../CartItemTile.constants';
import styles from '../styles/ProductTileWrapper.style';
import { parseStoreHours } from '../../../../../../../utils';
import CartItemTileLabels, {
  CHECKOUT_LABELS,
  GLOBAL_LABELS,
} from '../../../molecules/CartItemTile/views/CartItemTile.labels';
import {
  GlobalGenericLabels,
  GLOBAL_GENERIC_LABELS,
  lblShipping,
} from '../../../../../../../utils/labels/globalGeneric.labels';
import {
  globalCommonBrowseLabels,
  GLOBAL_COMMON_BROWSE_LABELS,
} from '../../../../../../../utils/labels/globalCommonBrowseLabels.labels';
import SocialProofBanner from '../../../../common/organism/SocialProofBanner';
import BagpageUtils from '../../../../BagPage/views/Bagpage.utils';
import { PLPLabels } from '../../../../../../../utils/labels/browse/PLP.labels';

export const getDateRange = (preOrderDates) => {
  const maxDateObj = getDateObject(Math.max.apply(null, preOrderDates));
  const minDateObj = getDateObject(Math.min.apply(null, preOrderDates));
  let rangeDate = null;
  if (maxDateObj && minDateObj) {
    rangeDate = `${getFormattedPreOrderDate(
      dateFormatter(minDateObj)
    )} - ${getFormattedPreOrderDate(dateFormatter(maxDateObj))}`;
  }
  if (minDateObj.getTime() === maxDateObj.getTime()) {
    rangeDate = getFormattedPreOrderDate(dateFormatter(minDateObj));
  }
  return rangeDate;
};

export const getGroupDetails = (type) => {
  const globalCommonBrowse = getLabelFromCopyOrFromTranslation(
    GLOBAL_COMMON_BROWSE_LABELS,
    globalCommonBrowseLabels
  );

  const { ECOM, BOPIS, BOSS } = CARTPAGE_CONSTANTS;
  let label;
  let icon;
  if (type === ECOM) {
    label = renderLabel(
      getLabelFromCopyOrFromTranslation(
        `${GLOBAL_GENERIC_LABELS}.${lblShipping}`,
        GlobalGenericLabels
      ),
      'lbl_shipping'
    );
    icon = 'shipping-section';
  } else if (type === BOPIS) {
    label = renderLabel(globalCommonBrowse?.lbl_pick_up, 'lbl_pick_up');
    icon = 'pickup-section';
  }
  if (type === BOSS) {
    label = renderLabel(
      getDynamicLabelValues('dynamic.browse_d.lbl_no_rush_pick_up'),
      'lbl_no_rush_pick_up'
    );
    icon = 'no-rush-section';
  }
  return {
    label,
    icon,
  };
};

const preOrderSection = (preOrderData) => {
  const checkoutLabels = getLabelFromCopyOrFromTranslation(CHECKOUT_LABELS, CartItemTileLabels);
  const { bagPage } = checkoutLabels;

  return (
    <div className="section">
      <BodyCopy
        fontSize={['fs12', 'fs12', 'fs14']}
        className="elem-mt-MED"
        fontFamily="secondary"
        fontWeight="semibold"
        color="green.500"
      >
        {renderLabel(bagPage.lbl_preorder_items_edd, 'lbl_preorder_items_edd')}
        <BodyCopy
          component="span"
          fontSize={['fs12', 'fs12', 'fs14']}
          className="elem-pl-XXS elem-mt-MED"
          fontFamily="secondary"
          fontWeight="extrabold"
          color="green.500"
        >
          {`${preOrderData}`}
        </BodyCopy>
      </BodyCopy>
    </div>
  );
};

const getOrderTypeValue = (orderType) => {
  const { ECOM, BOPIS, BOSS, HOME } = CARTPAGE_CONSTANTS;
  let value;
  if (orderType === ECOM) {
    value = HOME;
  } else if (orderType === BOPIS) {
    value = BOPIS?.toLowerCase();
  } else if (orderType === BOSS) {
    value = BOSS?.toLowerCase();
  }
  return value;
};

const changeStore = (e, onPickUpOpenClick, props) => {
  if (e) {
    e.preventDefault();
  }
  const { firstItemInfo, orderType, storeInformation, value } = props;
  onPickUpOpenClick({
    fromBagPage: true,
    fromChangeStore: true,
    currentProduct: {
      ...firstItemInfo,
      c_orderItemType: value || orderType,
      toggle: value || orderType,
    },
    isProductPickup: true,
    isBopisPickup: value ? value !== 'boss' : getOrderTypeValue(orderType) === 'bopis',
    pickupSelectedStore: storeInformation,
  });
};

const isEnterKey = (e) => {
  return e.keyCode === 13;
};

export const getStoreInformation = ({ shippingAddress = '', c_fromStoreId: fromStoreId }) => {
  if (shippingAddress) {
    const {
      address1 = '',
      city = '',
      phone = '',
      postalCode = '',
      c_storeName: storeName = '',
      stateCode = '',
      countryCode = '',
      c_storeHours: storeHours,
    } = shippingAddress;
    const { storehours } = storeHours ? JSON.parse(storeHours) : { storehours: [] };
    return {
      basicInfo: {
        address: {
          addressLine1: address1,
          city,
          country: countryCode,
          state: stateCode,
          zipCode: postalCode,
        },
        phone,
        storeName,
        id: fromStoreId,
      },
      hours: {
        regularHours: [],
        holidayHours: [],
        regularAndHolidayHours: parseStoreHours(storehours),
      },
    };
  }
  return null;
};

const defaultStoreSection = (
  storeName,
  capitalizeCityName,
  state,
  onPickUpOpenClick,
  openPickupModalProps,
  isBopisEnabled,
  isDesktopProductTileCleanUpDisabled
) => {
  const globalLabels = getLabelFromCopyOrFromTranslation(GLOBAL_LABELS, CartItemTileLabels);
  const { cartItemTile } = globalLabels || {};
  const { isBossEnabled } = openPickupModalProps;

  return (
    <div
      className={
        isDesktopProductTileCleanUpDisabled
          ? 'store-details-container container'
          : 'store-details-container-new-design container'
      }
    >
      <div>
        {!isDesktopProductTileCleanUpDisabled && (
          <BodyCopy
            component="span"
            fontSize={['fs12', 'fs12', 'fs14']}
            fontFamily="secondary"
            fontWeight="medium"
          >
            {`Pick-Up at `}
          </BodyCopy>
        )}
        <BodyCopy
          component="span"
          fontSize={['fs12', 'fs12', 'fs14']}
          fontFamily="secondary"
          fontWeight={isDesktopProductTileCleanUpDisabled ? 'extrabold' : 'bold'}
          className="capFirstLetter store-icon"
        >
          {isDesktopProductTileCleanUpDisabled
            ? `${storeName?.toLowerCase()} | `
            : `${storeName?.toLowerCase()}`}
        </BodyCopy>
        <BodyCopy
          component="span"
          fontSize={['fs12', 'fs12', 'fs14']}
          fontFamily="secondary"
          fontWeight={isDesktopProductTileCleanUpDisabled ? 'semibold' : 'bold'}
        >
          {isDesktopProductTileCleanUpDisabled
            ? `${capitalizeCityName}, ${state}`
            : `, ${capitalizeCityName}, ${state}`}
        </BodyCopy>
      </div>
      {(isBossEnabled || isBopisEnabled) && (
        <div>
          <Anchor
            fontSizeVariation="medium"
            className="change-store-link"
            role="button"
            tabIndex="0"
            underline
            noLink
            handleLinkClick={(e) => changeStore(e, onPickUpOpenClick, openPickupModalProps)}
            onKeyDown={(e) => {
              if (isEnterKey(e)) {
                changeStore(e, onPickUpOpenClick, openPickupModalProps);
              }
            }}
          >
            {renderLabel(
              cartItemTile.lbl_cartTile_changeStore,
              'lbl_Product_pickup_CHANGE_STORE_DM'
            )}
          </Anchor>
        </div>
      )}
    </div>
  );
};
const getClasses = (isMiniBag) => {
  return {
    containerCSS: `container main-products-container ${!isMiniBag ? '' : 'mini-container-pt'}`,
    rightSectionCSS: `section rgt-content ${!isMiniBag ? '' : 'mini-section'}`,
    wrapperClass: `${!isMiniBag ? 'group-wrapper' : 'mini-group-wrapper'}`,
    iconClass: `${!isMiniBag ? 'elem-pl-XS elem-pr-XS' : 'mini-icon'}`,
    fontClass1: `${!isMiniBag ? ['fs20', 'fs18', 'fs28'] : 'fs16'}`,
    fontClass2: `${!isMiniBag ? ['fs14', 'fs14', 'fs16'] : 'fs12'}`,
  };
};

const shouldShowBossTile = (isBossEnabled, isBossCart, userDefaultStore) => {
  if (!isCanada()) {
    const isBossEligibleStore = userDefaultStore;
    if (isBossCart) {
      return true;
    }
    return isBossEnabled && isBossEligibleStore;
  }
  return false;
};

// eslint-disable-next-line sonarjs/cognitive-complexity, complexity
const ProductTileGroup = (props) => {
  const {
    className,
    orderType,
    items,
    shipment,
    isNewQVEnabled,
    quickViewLabels,
    isBossEnabled,
    userDefaultStore,
    totalItemsQty,
    groupItemsQty,
    shippingMethodSelected,
    preOrderDates,
    newBag,
    firstItemInfo,
    updateStoreItem,
    isMiniBag,
    loading,
    setStoreUpdateCompletedState,
    pickUpTileEnabled,
    orderEddDates,
    onPickUpOpenClick,
    isBopisEnabled,
    isBossCart,
    isBopisCart,
    isPreOrderItemsIncluded,
    shipments,
    freeShippingThershold,
    shippingTotal,
    isDynamicFreeShippingEnabled,
    productTotal,
    isSiteWidePromoBannerAbTestEnabled,
    isExtendedHolidayReturnsBannerAbTestEnabled,
    isHolidayGetItByMessageBannerAbTestEnabled,
    isDefaultSocialProofBanner,
    setDefaultBannerAction,
    timerDeadlineValue,
    isDesktopProductTileCleanUpDisabled,
    isBopisPromoDiscountBannerEnabled,
    storeHoursThreshold,
    isDisplayPickupTimeEnabled,
    isFullDateDisplayEnabled,
    isInternationalShipping,
    isEnableShipmentTilesForInternationalSite,
    isMixedOrder,
    setUserDefaultStore,
    isMobileWebTileEnhancementEnabled,
  } = props;
  const [changedOrderType, setChangedOrderType] = useState(null);
  const { ECOM, HOME, BOSS } = CARTPAGE_CONSTANTS;
  const { storeBossInfo: { isBossEligible = false } = {} } = userDefaultStore || {};
  const showBossTile = shouldShowBossTile(isBossEnabled, isBossCart, userDefaultStore);
  const isBopisOrBoss =
    orderType === 'PURE_BOPIS' ||
    orderType === 'BOPIS' ||
    orderType === 'PURE_BOSS' ||
    orderType === 'BOSS';

  const hideFulfillmentTile =
    !isMixedOrder &&
    !isBopisOrBoss &&
    isInternationalShipping &&
    isEnableShipmentTilesForInternationalSite;
  const showFulfilmentTiles = pickUpTileEnabled || isBopisCart || showBossTile;
  const storeInformation = getStoreInformation(shipment);

  const pickupSelectionUpdate = (value) => {
    if (orderType !== value) {
      setStoreUpdateCompletedState('');
      setChangedOrderType(value);
      const changeOrderType = value === HOME ? ECOM : value?.toUpperCase();
      if (!isBossCart && !isBossEligible && value?.toUpperCase() === BOSS) {
        changeStore(null, onPickUpOpenClick, {
          isBossEnabled,
          firstItemInfo,
          orderType,
          value,
          storeInformation,
        });
      } else if (firstItemInfo) {
        let storeId = userDefaultStore?.basicInfo?.id;
        const altStoreId = userDefaultStore?.getIn?.(['basicInfo', 'id']);

        if (!storeId && storeInformation) {
          storeId = storeInformation.basicInfo?.id;
          setUserDefaultStore(storeInformation);
        }
        const payload = {
          ...firstItemInfo,
          c_orderItemType: changeOrderType,
          c_fromStoreId: storeId,
          toggle: changeOrderType,
          isScrollToTile: true,
        };
        updateStoreItem(payload);
        if (!storeId && altStoreId) {
          const errorTag = 'Missing Store Id';
          logger.error({
            error: errorTag,
            errorTags: [errorTag],
            extraData: {
              altStoreId,
              orderItemType: changeOrderType,
              userDefaultStore: JSON.stringify(userDefaultStore),
              shipments: JSON.stringify(shipments),
              Component: 'ProductTileGroup Web',
            },
          });
        }
      }
    }
  };

  const { basicInfo: { storeName, address: { city, state } = {} } = {} } = storeInformation || {};
  const capitalizeCityName = city?.charAt(0).toUpperCase() + city?.slice(1);
  const { label = '', icon = '' } = getGroupDetails(orderType);
  const preOrderData = preOrderDates?.length > 0 && getDateRange(preOrderDates);
  const { containerCSS, rightSectionCSS, wrapperClass, iconClass, fontClass1, fontClass2 } =
    getClasses(isMiniBag);

  const richTextHtmlDefault = getDynamicLabelValues('dynamic.cart.lbl_social_proofing_msg');
  const richTextHtml1 = getDynamicLabelValues('dynamic.cart.lbl_social_proofing_msg2');
  const richTextHtml2 = getDynamicLabelValues('dynamic.cart.lbl_social_proofing_msg3');
  const richTextHtml3 = getDynamicLabelValues('dynamic.cart.lbl_social_proofing_msg4');
  const itemsText = getLabelFromCopyOrFromTranslation('PLP.plpTiles.lbl_items', PLPLabels);
  const isDeadlinePassed = BagpageUtils.isDeadlinePassed(
    timerDeadlineValue,
    isExtendedHolidayReturnsBannerAbTestEnabled
  );

  return (
    <div className={className}>
      <div className={wrapperClass}>
        {newBag && (
          <>
            <div className={containerCSS}>
              <div className={rightSectionCSS}>
                <div className="section">
                  <Image alt={orderType} className={iconClass} src={getIconPath(icon)} />
                  <BodyCopy
                    fontSize={fontClass1}
                    fontFamily="secondary"
                    fontWeight="extrabold"
                    className={orderType === ECOM ? 'shipping-text' : 'delivery-methods-text'}
                  >
                    {label}
                  </BodyCopy>
                </div>
                <div className={!isMiniBag ? '' : 'mini-item-count'}>
                  <BodyCopy
                    fontSize={fontClass2}
                    className="elem-pl-SM total-items"
                    fontFamily="secondary"
                    fontWeight="semibold"
                  >
                    {`${groupItemsQty} / ${totalItemsQty} ${itemsText}`}
                  </BodyCopy>
                </div>
              </div>
              {!isMiniBag && showFulfilmentTiles && !hideFulfillmentTile && (
                <div className="section lft-content">
                  <DeliveryMethods
                    isPickUpSelected={getOrderTypeValue(orderType)}
                    isNewQVEnabled={isNewQVEnabled}
                    quickViewLabels={quickViewLabels}
                    pickupSelectionUpdate={pickupSelectionUpdate}
                    checkForOOSForVariant={false}
                    isOnlineExclusive={false}
                    isBossEnabled={showBossTile}
                    isAllSizeDisabled={false}
                    userDefaultStore={userDefaultStore}
                    shippingMethodSelected={shippingMethodSelected}
                    loading={loading}
                    changedOrderType={changedOrderType}
                    className="shipping-methods-container"
                    isBopisEnabled={pickUpTileEnabled}
                    fulfillmentGroup
                    orderEddDates={orderEddDates}
                    isCanada={isCanada()}
                    isCartTile={true}
                    newBag={newBag}
                    shipment={shipment}
                    isPreOrderItemsIncluded={isPreOrderItemsIncluded}
                    shipments={shipments}
                    freeShippingThershold={freeShippingThershold}
                    shippingTotal={shippingTotal}
                    isDynamicFreeShippingEnabled={isDynamicFreeShippingEnabled}
                    orderType={orderType}
                    productTotal={productTotal}
                    isBopisPromoDiscountBannerEnabled={isBopisPromoDiscountBannerEnabled}
                    storeHoursThreshold={storeHoursThreshold}
                    isDisplayPickupTimeEnabled={isDisplayPickupTimeEnabled}
                    isFullDateDisplayEnabled={isFullDateDisplayEnabled}
                    storeInformation={storeInformation}
                  />
                </div>
              )}
            </div>
            {storeInformation &&
              !isMiniBag &&
              defaultStoreSection(
                storeName,
                capitalizeCityName,
                state,
                onPickUpOpenClick,
                {
                  isBossEnabled,
                  firstItemInfo,
                  orderType,
                  storeInformation,
                },
                isBopisEnabled,
                isDesktopProductTileCleanUpDisabled
              )}
            {preOrderData && !isMiniBag && showFulfilmentTiles && preOrderSection(preOrderData)}
          </>
        )}
        {items}
        {isMobileWeb() && (
          <SocialProofBanner
            richTextHtmlDefault={richTextHtmlDefault}
            richTextHtml1={richTextHtml1}
            richTextHtml2={richTextHtml2}
            richTextHtml3={richTextHtml3}
            isSiteWidePromoBannerAbTestEnabled={isSiteWidePromoBannerAbTestEnabled}
            isExtendedHolidayReturnsBannerAbTestEnabled={
              isExtendedHolidayReturnsBannerAbTestEnabled
            }
            isHolidayGetItByMessageBannerAbTestEnabled={isHolidayGetItByMessageBannerAbTestEnabled}
            isDefaultSocialProofBanner={isDeadlinePassed || isDefaultSocialProofBanner}
            setDefaultBannerAction={setDefaultBannerAction}
            timerDeadlineValue={timerDeadlineValue}
            isMobileWebTileEnhancementEnabled={isMobileWebTileEnhancementEnabled}
          />
        )}
      </div>
    </div>
  );
};

ProductTileGroup.propTypes = {
  className: PropTypes.string.isRequired,
  orderType: PropTypes.string.isRequired,
  userDefaultStore: PropTypes.shape({
    basicInfo: PropTypes.shape({
      id: PropTypes.string.isRequired,
      storeName: PropTypes.string.isRequired,
    }).isRequired,
  }),
  isNewQVEnabled: PropTypes.bool,
  quickViewLabels: PropTypes.shape({}),
  isBossEnabled: PropTypes.bool,
  totalItemsQty: PropTypes.number,
  groupItemsQty: PropTypes.number,
  shippingMethodSelected: PropTypes.string,
  preOrderDates: PropTypes.shape({}).isRequired,
  newBag: PropTypes.bool,
  isMiniBag: PropTypes.bool,
  shipment: PropTypes.shape({}),
  loading: PropTypes.bool,
  pickUpTileEnabled: PropTypes.bool,
  isBopisPromoDiscountBannerEnabled: PropTypes.bool,
};

ProductTileGroup.defaultProps = {
  userDefaultStore: {
    basicInfo: {
      id: '',
      storeName: '',
    },
  },
  isNewQVEnabled: false,
  quickViewLabels: {},
  isBossEnabled: false,
  totalItemsQty: 0,
  groupItemsQty: 0,
  shippingMethodSelected: '',
  newBag: false,
  isMiniBag: false,
  shipment: {},
  loading: false,
  pickUpTileEnabled: false,
  isBopisPromoDiscountBannerEnabled: false,
};

export default withStyles(ProductTileGroup, styles);
export { ProductTileGroup as ProductTileGroupVanilla };
